import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useGetFeedMatchStatsQuery, type TeamStat } from '@/api/feed/getFeedMatchStats';
import { useGetFeedTeamsQuery } from '@/api/feed/getFeedTeams';
import { ArrowLeft, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { useCallback, useMemo } from 'react';
import { generateTeamLogoUrl, Sport } from '@/utils/jersey-mapping';

// --- Sport-specific stat configurations ---

type TeamStatEntry = {
  statKey: string;
  displayName: string;
  format?: 'percentage' | 'fraction' | 'number';
};

type PlayerStatEntry = {
  statKey: string;
  displayName: string;
  column: string;
  format?: 'number' | 'fraction' | 'percentage' | 'minutes';
};

// NBA / NCAAMB team stats
export const nbaTeamStatConfig: TeamStatEntry[] = [
  { statKey: 'points', displayName: 'Points', format: 'number' },
  { statKey: 'field_goals_made', displayName: 'Field Goals Made', format: 'fraction' },
  { statKey: 'field_goals_att', displayName: 'Field Goals Attempted', format: 'number' },
  { statKey: 'field_goals_pct', displayName: 'FG %', format: 'percentage' },
  { statKey: 'three_points_made', displayName: '3PT Made', format: 'fraction' },
  { statKey: 'three_points_att', displayName: '3PT Attempted', format: 'number' },
  { statKey: 'three_points_pct', displayName: '3PT %', format: 'percentage' },
  { statKey: 'free_throws_made', displayName: 'Free Throws Made', format: 'fraction' },
  { statKey: 'free_throws_att', displayName: 'Free Throws Attempted', format: 'number' },
  { statKey: 'free_throws_pct', displayName: 'FT %', format: 'percentage' },
  { statKey: 'rebounds', displayName: 'Total Rebounds', format: 'number' },
  { statKey: 'assists', displayName: 'Assists', format: 'number' },
  { statKey: 'turnovers', displayName: 'Turnovers', format: 'number' },
  { statKey: 'steals', displayName: 'Steals', format: 'number' },
  { statKey: 'blocks', displayName: 'Blocks', format: 'number' },
  { statKey: 'personal_fouls', displayName: 'Team Fouls', format: 'number' },
];

// MLB team stats
export const mlbTeamStatConfig: TeamStatEntry[] = [
  { statKey: 'runs', displayName: 'Runs', format: 'number' },
  { statKey: 'hits', displayName: 'Hits', format: 'number' },
  { statKey: 'errors', displayName: 'Errors', format: 'number' },
];

// NFL team stats
export const nflTeamStatConfig: TeamStatEntry[] = [
  { statKey: 'points', displayName: 'Points', format: 'number' },
  { statKey: 'pass_yards', displayName: 'Pass Yards', format: 'number' },
  { statKey: 'rush_yards', displayName: 'Rush Yards', format: 'number' },
  { statKey: 'pass_touchdowns', displayName: 'Pass TDs', format: 'number' },
  { statKey: 'rush_touchdowns', displayName: 'Rush TDs', format: 'number' },
  // Feed emits 'penalty_penalties' (plural); 'turnovers' isn't aggregated for NFL
  // yet, so it's omitted rather than shown as a permanent DNP.
  { statKey: 'penalty_penalties', displayName: 'Penalties', format: 'number' },
  { statKey: 'penalty_yards', displayName: 'Penalty Yards', format: 'number' },
];

// NBA / NCAAMB player stats
export const nbaPlayerStatConfig: PlayerStatEntry[] = [
  { statKey: 'full_name', displayName: 'Player', column: 'Player Name' },
  { statKey: 'points', displayName: 'PTS', column: 'Points', format: 'number' },
  { statKey: 'rebounds', displayName: 'REB', column: 'Total Rebounds', format: 'number' },
  { statKey: 'assists', displayName: 'AST', column: 'Assists', format: 'number' },
  { statKey: 'steals', displayName: 'STL', column: 'Steals', format: 'number' },
  { statKey: 'blocks', displayName: 'BLK', column: 'Blocks', format: 'number' },
  { statKey: 'turnovers', displayName: 'TO', column: 'Turnovers', format: 'number' },
  { statKey: 'field_goals_made', displayName: 'FG', column: 'Made', format: 'fraction' },
  { statKey: 'minutes', displayName: 'MIN', column: 'Minutes Played', format: 'minutes' },
];

// MLB hitter stats
export const mlbHitterStatConfig: PlayerStatEntry[] = [
  { statKey: 'full_name', displayName: 'Player', column: 'Player Name' },
  { statKey: 'hitting_ab', displayName: 'AB', column: 'At Bats', format: 'number' },
  { statKey: 'hitting_runs', displayName: 'R', column: 'Runs', format: 'number' },
  { statKey: 'hitting_h', displayName: 'H', column: 'Hits', format: 'number' },
  { statKey: 'hitting_doubles', displayName: '2B', column: 'Doubles', format: 'number' },
  { statKey: 'hitting_triples', displayName: '3B', column: 'Triples', format: 'number' },
  { statKey: 'hitting_rbi', displayName: 'RBI', column: 'RBI', format: 'number' },
  { statKey: 'hitting_hr', displayName: 'HR', column: 'Home Runs', format: 'number' },
  { statKey: 'hitting_bb', displayName: 'BB', column: 'Walks', format: 'number' },
  { statKey: 'hitting_so', displayName: 'K', column: 'Strikeouts', format: 'number' },
  { statKey: 'hitting_sb', displayName: 'SB', column: 'Stolen Bases', format: 'number' },
  { statKey: 'hitting_avg', displayName: 'AVG', column: 'Batting Average', format: 'number' },
];

// MLB pitcher stats
export const mlbPitcherStatConfig: PlayerStatEntry[] = [
  { statKey: 'full_name', displayName: 'Player', column: 'Player Name' },
  { statKey: 'pitching_ip_1', displayName: 'IP', column: 'Innings Pitched', format: 'number' },
  { statKey: 'pitching_hits', displayName: 'H', column: 'Hits', format: 'number' },
  { statKey: 'pitching_er', displayName: 'ER', column: 'Earned Runs', format: 'number' },
  { statKey: 'pitching_bb', displayName: 'BB', column: 'Walks', format: 'number' },
  { statKey: 'pitching_k', displayName: 'K', column: 'Strikeouts', format: 'number' },
  { statKey: 'pitching_era', displayName: 'ERA', column: 'ERA', format: 'number' },
];

// NFL player stats
export const nflPlayerStatConfig: PlayerStatEntry[] = [
  { statKey: 'full_name', displayName: 'Player', column: 'Player Name' },
  { statKey: 'pass_yards', displayName: 'Pass Yds', column: 'Pass Yards', format: 'number' },
  { statKey: 'pass_touchdowns', displayName: 'Pass TD', column: 'Pass TDs', format: 'number' },
  { statKey: 'rush_yards', displayName: 'Rush Yds', column: 'Rush Yards', format: 'number' },
  { statKey: 'rush_touchdowns', displayName: 'Rush TD', column: 'Rush TDs', format: 'number' },
  { statKey: 'receive_yards', displayName: 'Rec Yds', column: 'Receiving Yards', format: 'number' },
];

// Soccer team stats (DKPLAT-356). Stat keys mirror what
// statistics.service.ts:aggregateSoccerTeamStats emits — see the
// `soccer_team_*` / `soccer_*` codes in
// apps/feed-service/src/config/stat-types.config.ts.
export const soccerTeamStatConfig: TeamStatEntry[] = [
  { statKey: 'soccer_team_goals', displayName: 'Goals', format: 'number' },
  { statKey: 'soccer_team_shots', displayName: 'Shots', format: 'number' },
  { statKey: 'soccer_team_shots_on_target', displayName: 'Shots on Target', format: 'number' },
  { statKey: 'soccer_possession', displayName: 'Possession', format: 'percentage' },
  { statKey: 'soccer_corners', displayName: 'Corners', format: 'number' },
  { statKey: 'soccer_fouls', displayName: 'Fouls', format: 'number' },
  { statKey: 'soccer_team_yellow_cards', displayName: 'Yellow Cards', format: 'number' },
  { statKey: 'soccer_team_red_cards', displayName: 'Red Cards', format: 'number' },
];

// Soccer player stats (DKPLAT-356)
export const soccerPlayerStatConfig: PlayerStatEntry[] = [
  { statKey: 'full_name', displayName: 'Player', column: 'Player Name' },
  { statKey: 'soccer_goals', displayName: 'G', column: 'Goals', format: 'number' },
  { statKey: 'soccer_assists', displayName: 'A', column: 'Assists', format: 'number' },
  { statKey: 'soccer_shots', displayName: 'Shots', column: 'Shots', format: 'number' },
  {
    statKey: 'soccer_shots_on_target',
    displayName: 'SoT',
    column: 'Shots on Target',
    format: 'number',
  },
  { statKey: 'soccer_yellow_card', displayName: 'YC', column: 'Yellow Cards', format: 'number' },
  { statKey: 'soccer_red_card', displayName: 'RC', column: 'Red Cards', format: 'number' },
];

type SportConfig = {
  teamStats: TeamStatEntry[];
  playerStats: PlayerStatEntry[];
  pitcherStats: PlayerStatEntry[] | null;
  sortKey: string;
  pitcherSortKey?: string;
};

function getStatConfigForSport(leagueId: string): SportConfig {
  const sport = leagueId.toLowerCase();
  if (sport === 'mlb') {
    return {
      teamStats: mlbTeamStatConfig,
      playerStats: mlbHitterStatConfig,
      pitcherStats: mlbPitcherStatConfig,
      sortKey: 'hitting_ab',
      pitcherSortKey: 'pitching_ip_1',
    };
  }
  if (sport === 'nfl') {
    return {
      teamStats: nflTeamStatConfig,
      playerStats: nflPlayerStatConfig,
      pitcherStats: null,
      sortKey: 'pass_yards',
    };
  }
  if (sport === 'soccer') {
    return {
      teamStats: soccerTeamStatConfig,
      playerStats: soccerPlayerStatConfig,
      pitcherStats: null,
      sortKey: 'soccer_goals',
    };
  }
  // NBA, NCAAMB, and any unknown sport
  return {
    teamStats: nbaTeamStatConfig,
    playerStats: nbaPlayerStatConfig,
    pitcherStats: null,
    sortKey: 'points',
  };
}

export const Route = createFileRoute('/dashboard/sports/$id/matches/$matchId/stats')({
  component: RouteComponent,
});
export function RouteComponent() {
  const { id, matchId } = Route.useParams();
  const navigate = useNavigate();
  const leagueId = id.toLowerCase();

  const { data: statsData, isLoading } = useGetFeedMatchStatsQuery(matchId);
  const { data: teams = [] } = useGetFeedTeamsQuery(leagueId);

  const sportConfig = getStatConfigForSport(leagueId);
  const isMLB = leagueId === 'mlb';

  // Create a map of team IDs to team data for logos
  const teamMap = useMemo(() => {
    const map = new Map();
    teams.forEach(team => {
      map.set(team.externalId, {
        name: team.name,
        abbreviation: team.abbreviation || 'UNK',
        logoUrl: generateTeamLogoUrl(team.abbreviation || '', leagueId as Sport),
      });
    });
    return map;
  }, [teams, leagueId]);

  const homeTeam = statsData
    ? teamMap.get(statsData.match.homeTeamId) || {
        name: statsData.match.homeTeamName,
        abbreviation: statsData.match.homeTeamAbbreviation,
        logoUrl: generateTeamLogoUrl(statsData.match.homeTeamAbbreviation || '', leagueId as Sport),
      }
    : null;
  const awayTeam = statsData
    ? teamMap.get(statsData.match.awayTeamId) || {
        name: statsData.match.awayTeamName,
        abbreviation: statsData.match.awayTeamAbbreviation,
        logoUrl: generateTeamLogoUrl(statsData.match.awayTeamAbbreviation || '', leagueId as Sport),
      }
    : null;

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      scheduled: 'Upcoming',
      live: 'In Play',
      finished: 'FINISHED',
      postponed: 'Postponed',
      cancelled: 'Cancelled',
    };
    return statusMap[status] || status;
  };

  const getTeamStatValue = (stats: TeamStat[], statKey: string): number | null => {
    const stat = stats.find(s => s.statType === statKey);
    return stat?.value ?? null;
  };

  const getTeamStatFraction = (
    stats: TeamStat[],
    madeKey: string,
    attKey: string
  ): string | null => {
    const made = stats.find(s => s.statType === madeKey)?.value;
    const att = stats.find(s => s.statType === attKey)?.value;
    if (made !== undefined && att !== undefined) {
      return `${made}/${att}`;
    }
    return null;
  };

  const formatStatValue = (
    value: number | string | null,
    format?: 'percentage' | 'fraction' | 'number' | 'minutes'
  ): string => {
    // A player listed in the boxscore played; a missing value in a given stat
    // category (e.g. a QB's receiving line) means "no stat here", not "did not
    // play". Show a dash rather than the misleading "DNP".
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value;
    if (format === 'percentage') {
      return `${value.toFixed(1)}%`;
    }
    if (format === 'minutes') {
      return value.toString();
    }
    return value.toString();
  };

  const getPlayerStatValue = (
    playerStats: Record<string, number | string>,
    statKey: string
  ): number | string | null => {
    return playerStats[statKey] ?? null;
  };

  const getPlayerStatFraction = (
    playerStats: Record<string, number | string>,
    madeKey: string,
    attKey: string
  ): string | null => {
    const made = playerStats[madeKey];
    const att = playerStats[attKey];
    if (made !== undefined && att !== undefined) {
      return `${made}/${att}`;
    }
    return null;
  };

  type PlayerEntry = {
    playerId: string;
    playerName: string;
    stats: Record<string, number | string>;
  };

  // Sort and filter players based on sport
  const sortPlayers = useCallback((players: PlayerEntry[], sortKey: string): PlayerEntry[] => {
    return [...players].sort((a, b) => {
      const aRaw = a.stats[sortKey];
      const bRaw = b.stats[sortKey];
      const aVal = typeof aRaw === 'number' ? aRaw : parseFloat(String(aRaw)) || 0;
      const bVal = typeof bRaw === 'number' ? bRaw : parseFloat(String(bRaw)) || 0;
      return bVal - aVal;
    });
  }, []);

  // For MLB, split players into hitters and pitchers
  const filterHitters = useCallback((players: PlayerEntry[]): PlayerEntry[] => {
    return players.filter(p => Object.keys(p.stats).some(k => k.startsWith('hitting_')));
  }, []);

  const filterPitchers = useCallback((players: PlayerEntry[]): PlayerEntry[] => {
    return players.filter(p => Object.keys(p.stats).some(k => k.startsWith('pitching_')));
  }, []);

  const sortedHomePlayerStats = useMemo(() => {
    if (!statsData) return [];
    return sortPlayers(statsData.playerStats.home as PlayerEntry[], sportConfig.sortKey);
  }, [statsData, sportConfig.sortKey, sortPlayers]);

  const sortedAwayPlayerStats = useMemo(() => {
    if (!statsData) return [];
    return sortPlayers(statsData.playerStats.away as PlayerEntry[], sportConfig.sortKey);
  }, [statsData, sportConfig.sortKey, sortPlayers]);

  // MLB-specific: split into hitters/pitchers
  const homeHitters = useMemo(() => {
    if (!statsData || !isMLB) return [];
    return sortPlayers(filterHitters(statsData.playerStats.home as PlayerEntry[]), 'hitting_ab');
  }, [statsData, isMLB, filterHitters, sortPlayers]);

  const awayHitters = useMemo(() => {
    if (!statsData || !isMLB) return [];
    return sortPlayers(filterHitters(statsData.playerStats.away as PlayerEntry[]), 'hitting_ab');
  }, [statsData, isMLB, filterHitters, sortPlayers]);

  const homePitchers = useMemo(() => {
    if (!statsData || !isMLB) return [];
    return sortPlayers(
      filterPitchers(statsData.playerStats.home as PlayerEntry[]),
      'pitching_ip_1'
    );
  }, [statsData, isMLB, filterPitchers, sortPlayers]);

  const awayPitchers = useMemo(() => {
    if (!statsData || !isMLB) return [];
    return sortPlayers(
      filterPitchers(statsData.playerStats.away as PlayerEntry[]),
      'pitching_ip_1'
    );
  }, [statsData, isMLB, filterPitchers, sortPlayers]);

  // Render a player stats table
  const renderPlayerTable = (title: string, players: PlayerEntry[], config: PlayerStatEntry[]) => (
    <ContentCard>
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      <Table>
        <TableHeader>
          <TableRow>
            {config.map(c => (
              <TableHead key={c.statKey} className={c.statKey !== 'full_name' ? 'text-center' : ''}>
                {c.displayName}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {players.map(player => (
            <TableRow key={player.playerId}>
              {config.map(c => {
                // Default to a dash for consistency with formatStatValue — a
                // listed player with a missing value in a category means "no
                // stat here", not "did not play".
                let displayValue: string = '-';

                if (c.statKey === 'full_name') {
                  displayValue = player.playerName;
                } else if (c.format === 'fraction' && c.statKey === 'field_goals_made') {
                  const fgValue = getPlayerStatFraction(
                    player.stats,
                    'field_goals_made',
                    'field_goals_att'
                  );
                  displayValue = fgValue ?? '-';
                } else {
                  const value = getPlayerStatValue(player.stats, c.statKey);
                  displayValue = formatStatValue(value, c.format);
                }

                return (
                  <TableCell
                    key={c.statKey}
                    className={c.statKey !== 'full_name' ? 'text-center' : 'font-medium'}
                  >
                    {displayValue}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ContentCard>
  );

  const breadcrumbs = (
    <Breadcrumbs
      overrides={{
        sports: 'Sports',
        [id]: id.toUpperCase(),
        matches: 'Matches',
        [matchId]: 'Match',
      }}
      pathOverrides={{
        sports: `/dashboard/sports/${id}`,
        [id]: `/dashboard/sports/${id}`,
        matches: null,
        [matchId]: null,
      }}
    />
  );

  if (isLoading) {
    return (
      <PageContainer>
        {breadcrumbs}
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </PageContainer>
    );
  }

  if (!statsData) {
    return (
      <PageContainer>
        {breadcrumbs}
        <ContentCard>
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-400">Match stats not found</p>
            <Button
              variant="outline"
              onClick={() => navigate({ to: '/dashboard/sports/$id', params: { id } })}
              className="mt-4"
            >
              Back to Matches
            </Button>
          </div>
        </ContentCard>
      </PageContainer>
    );
  }

  const { match, teamStats } = statsData;

  // Some competitions have no detailed-stats coverage from the feed (e.g.
  // Sportradar tags international friendlies with `basic_team_stats: false`),
  // so the summary carries only the score — no per-team or per-player stats.
  // Detect that by the absence of ANY player stats: a covered match always
  // returns players. When uncovered we skip the wall of "DNP" rows + empty
  // player tables and show one clear note instead (the score still renders
  // above, and the feed synthesises the Goals row from it).
  const hasPlayerStats = sortedHomePlayerStats.length > 0 || sortedAwayPlayerStats.length > 0;
  const noDetailedStats = !hasPlayerStats;

  return (
    <PageContainer>
      {breadcrumbs}
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate({ to: '/dashboard/sports/$id', params: { id } })}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Matches
        </Button>
      </div>

      {/* Match Details Section */}
      <ContentCard className="mb-6">
        <h2 className="text-xl font-bold mb-6">Match Details</h2>

        {/* Teams and Score */}
        <div className="flex items-center justify-between mb-8">
          {/* Home Team */}
          <div className="flex flex-row items-center gap-4 flex-1 justify-center">
            <span className="font-semibold text-lg">{match.homeTeamName}</span>
            {homeTeam?.logoUrl ? (
              <img
                src={homeTeam.logoUrl}
                alt={homeTeam.name}
                className="h-16 w-16 object-contain"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-16 w-16 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>

          {/* Score */}
          <div className="flex flex-col items-center gap-2 mx-8">
            <Badge
              variant="outline"
              className={`text-sm rounded-full ${
                match.status === 'finished'
                  ? 'bg-gray-200 text-gray-900 dark:bg-gray-600 dark:text-gray-100'
                  : match.status === 'live'
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : ''
              }`}
            >
              {getStatusLabel(match.status)}
            </Badge>
            <div className="flex items-center gap-2">
              <span className="text-4xl font-bold text-purple-600 dark:text-purple-400">
                {match.homeScore ?? 0}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {match.status === 'finished' ? 'Final' : match.clock || ''}
              </span>
              <span className="text-4xl font-bold text-blue-600 dark:text-blue-400">
                {match.awayScore ?? 0}
              </span>
            </div>
          </div>

          {/* Away Team */}
          <div className="flex flex-row items-center gap-4 flex-1 justify-center">
            {awayTeam?.logoUrl ? (
              <img
                src={awayTeam.logoUrl}
                alt={awayTeam.name}
                className="h-16 w-16 object-contain"
                onError={e => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            ) : (
              <div className="h-16 w-16 rounded bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                <Users className="h-8 w-8 text-gray-400" />
              </div>
            )}
            <span className="font-semibold text-lg">{match.awayTeamName}</span>
          </div>
        </div>

        {/* Team Statistics */}
        <div className="space-y-4">
          {sportConfig.teamStats.map(config => {
            let homeValue: number | string | null = null;
            let awayValue: number | string | null = null;

            if (config.format === 'fraction' && config.statKey === 'field_goals_made') {
              homeValue = getTeamStatFraction(
                teamStats.home,
                'field_goals_made',
                'field_goals_att'
              );
              awayValue = getTeamStatFraction(
                teamStats.away,
                'field_goals_made',
                'field_goals_att'
              );
            } else if (config.format === 'fraction' && config.statKey === 'three_points_made') {
              homeValue = getTeamStatFraction(
                teamStats.home,
                'three_points_made',
                'three_points_att'
              );
              awayValue = getTeamStatFraction(
                teamStats.away,
                'three_points_made',
                'three_points_att'
              );
            } else if (config.format === 'fraction' && config.statKey === 'free_throws_made') {
              homeValue = getTeamStatFraction(
                teamStats.home,
                'free_throws_made',
                'free_throws_att'
              );
              awayValue = getTeamStatFraction(
                teamStats.away,
                'free_throws_made',
                'free_throws_att'
              );
            } else if (config.statKey === 'points') {
              // "Points" for a team = the game score. Some sports (NBA) expose it
              // as an aggregated team stat; others (NFL) don't, so fall back to the
              // score on the match. Only falls back when the stat is absent.
              homeValue = getTeamStatValue(teamStats.home, 'points') ?? match.homeScore ?? null;
              awayValue = getTeamStatValue(teamStats.away, 'points') ?? match.awayScore ?? null;
            } else {
              homeValue = getTeamStatValue(teamStats.home, config.statKey);
              awayValue = getTeamStatValue(teamStats.away, config.statKey);
            }

            // For bar graph, calculate percentages based on total (home + away)
            let homeNumeric: number = 0;
            let awayNumeric: number = 0;
            let totalValue: number = 0;
            let homePercentage: number = 0;
            let awayPercentage: number = 0;

            if (config.format === 'fraction') {
              const homeParts = typeof homeValue === 'string' ? homeValue.split('/') : [];
              const awayParts = typeof awayValue === 'string' ? awayValue.split('/') : [];

              const homeAtt = homeParts.length >= 2 ? parseFloat(homeParts[1]) || 0 : 0;
              const awayAtt = awayParts.length >= 2 ? parseFloat(awayParts[1]) || 0 : 0;

              const totalAtt = homeAtt + awayAtt;

              if (totalAtt > 0) {
                homePercentage = (homeAtt / totalAtt) * 100;
                awayPercentage = (awayAtt / totalAtt) * 100;
              }
            } else if (config.format === 'percentage') {
              let homeMade = 0;
              let homeAtt = 0;
              let awayMade = 0;
              let awayAtt = 0;

              if (config.statKey === 'field_goals_pct') {
                homeMade = getTeamStatValue(teamStats.home, 'field_goals_made') ?? 0;
                homeAtt = getTeamStatValue(teamStats.home, 'field_goals_att') ?? 0;
                awayMade = getTeamStatValue(teamStats.away, 'field_goals_made') ?? 0;
                awayAtt = getTeamStatValue(teamStats.away, 'field_goals_att') ?? 0;
              } else if (config.statKey === 'three_points_pct') {
                homeMade = getTeamStatValue(teamStats.home, 'three_points_made') ?? 0;
                homeAtt = getTeamStatValue(teamStats.home, 'three_points_att') ?? 0;
                awayMade = getTeamStatValue(teamStats.away, 'three_points_made') ?? 0;
                awayAtt = getTeamStatValue(teamStats.away, 'three_points_att') ?? 0;
              } else if (config.statKey === 'free_throws_pct') {
                homeMade = getTeamStatValue(teamStats.home, 'free_throws_made') ?? 0;
                homeAtt = getTeamStatValue(teamStats.home, 'free_throws_att') ?? 0;
                awayMade = getTeamStatValue(teamStats.away, 'free_throws_made') ?? 0;
                awayAtt = getTeamStatValue(teamStats.away, 'free_throws_att') ?? 0;
              }

              let homeDecimal = homeAtt > 0 ? (homeMade / homeAtt) * 100 : 0;
              let awayDecimal = awayAtt > 0 ? (awayMade / awayAtt) * 100 : 0;

              // Fallback: stats already expressed as a percentage value
              // (e.g. soccer_possession = 46) — use the raw values directly.
              if (homeDecimal === 0 && awayDecimal === 0) {
                homeDecimal =
                  typeof homeValue === 'number' ? homeValue : parseFloat(String(homeValue)) || 0;
                awayDecimal =
                  typeof awayValue === 'number' ? awayValue : parseFloat(String(awayValue)) || 0;
              }

              totalValue = homeDecimal + awayDecimal;

              if (totalValue > 0) {
                homePercentage = (homeDecimal / totalValue) * 100;
                awayPercentage = (awayDecimal / totalValue) * 100;
              }
            } else {
              homeNumeric =
                typeof homeValue === 'number' ? homeValue : parseFloat(String(homeValue)) || 0;
              awayNumeric =
                typeof awayValue === 'number' ? awayValue : parseFloat(String(awayValue)) || 0;

              totalValue = homeNumeric + awayNumeric;

              if (totalValue > 0) {
                homePercentage = (homeNumeric / totalValue) * 100;
                awayPercentage = (awayNumeric / totalValue) * 100;
              }
            }

            const formatPercentage = (value: number | string | null): string => {
              if (value === null) return 'DNP';
              const numValue = typeof value === 'number' ? value : parseFloat(String(value));
              if (isNaN(numValue)) return String(value);
              return `${numValue.toFixed(1)}%`;
            };

            const homeDisplay =
              homeValue === null
                ? 'DNP'
                : config.format === 'percentage'
                  ? formatPercentage(homeValue)
                  : String(homeValue);
            const awayDisplay =
              awayValue === null
                ? 'DNP'
                : config.format === 'percentage'
                  ? formatPercentage(awayValue)
                  : String(awayValue);

            // Uncovered competition: drop rows with no data rather than
            // rendering a wall of DNP. Rows that DO have data (e.g. Goals,
            // synthesised from the score) still render.
            if (noDetailedStats && homeValue === null && awayValue === null) {
              return null;
            }

            return (
              <div key={config.statKey} className="flex items-center gap-4">
                {/* Home Team Stat */}
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 flex justify-end">
                    <div className="flex items-center gap-2 w-full">
                      <span className="text-sm font-medium w-20 text-right">{homeDisplay}</span>
                      <div className="flex-1 h-6 bg-purple-100 dark:bg-purple-900 rounded">
                        <div
                          className="h-full bg-purple-600 dark:bg-purple-700 rounded ml-auto"
                          style={{ width: `${homePercentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Stat Label */}
                <div className="w-60 text-center">
                  <span className="text-sm font-medium">{config.displayName}</span>
                </div>

                {/* Away Team Stat */}
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 flex justify-start">
                    <div className="flex items-center gap-2 w-full">
                      <div className="flex-1 h-6 bg-blue-100 dark:bg-blue-900 rounded">
                        <div
                          className="h-full bg-blue-600 dark:bg-blue-700 rounded"
                          style={{ width: `${awayPercentage}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-20 text-left">{awayDisplay}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {noDetailedStats && (
          <div className="mt-4 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
            Detailed match &amp; player statistics aren&apos;t available for this competition (e.g.
            international friendlies have score-only feed coverage). Only the final score is shown.
          </div>
        )}
      </ContentCard>

      {/* Player Statistics Section — hidden when the feed has no player
          coverage for this competition (empty tables read as broken). */}
      {!hasPlayerStats ? null : isMLB ? (
        // MLB: 4 tables — Home Hitters, Home Pitchers, Away Hitters, Away Pitchers
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderPlayerTable(`${match.homeTeamName} — Hitters`, homeHitters, mlbHitterStatConfig)}
            {renderPlayerTable(`${match.awayTeamName} — Hitters`, awayHitters, mlbHitterStatConfig)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {renderPlayerTable(
              `${match.homeTeamName} — Pitchers`,
              homePitchers,
              mlbPitcherStatConfig
            )}
            {renderPlayerTable(
              `${match.awayTeamName} — Pitchers`,
              awayPitchers,
              mlbPitcherStatConfig
            )}
          </div>
        </div>
      ) : (
        // NBA, NCAAMB, NFL: 2 tables — Home / Away
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {renderPlayerTable(match.homeTeamName, sortedHomePlayerStats, sportConfig.playerStats)}
          {renderPlayerTable(match.awayTeamName, sortedAwayPlayerStats, sportConfig.playerStats)}
        </div>
      )}
    </PageContainer>
  );
}
